import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, storage } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { LinearProgress } from '@mui/material';
import imageCompression from 'browser-image-compression';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Card,
  Input,
  FormHelperText,
  IconButton,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BottomNavigationBar from './BottomNavigationBar';

const SettingsScreen = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    bio: '',
    sex: '',
    dob: null,
    phoneNumber: '',
    address: '',
    profilePhoto: null,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('You must be logged in to access settings.');
        navigate('/signin');
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            bio: data.bio || '',
            sex: data.sex || '',
            dob: data.dob ? new Date(data.dob) : null,
            phoneNumber: data.phoneNumber || '',
            address: data.address || '',
            profilePhoto: data.profilePhoto || null,
          });
        } else {
          setError('User profile not found.');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load profile data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Handle file selection for profile photo
const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (file) {
    const options = {
      maxSizeMB: 1,          // Max size 1MB
      maxWidthOrHeight: 800, // Max dimensions 800px
      useWebWorker: true
    };
    try {
      const compressedFile = await imageCompression(file, options);
      setSelectedFile(compressedFile);
    } catch (error) {
      console.log(error);
    }
  }
};

  // Handle form submission to update profile
  const handleSaveProfile = async () => {
  if (!userData.firstName.trim() || !userData.address.trim()) {
    setError('First Name and Address are required.');
    return;
  }

  // Use a separate state for saving vs loading initial data
  setIsLoading(true); 
  
  try {
    let profilePhotoUrl = userData.profilePhoto;

    if (selectedFile) {
      const storageRef = ref(storage, `profilePhotos/${auth.currentUser.uid}.jpg`);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      // We wrap this in a promise to wait for the URL
      profilePhotoUrl = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress); // This updates the progress bar
          },
          (error) => reject(error),
          async () => {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadUrl);
          }
        );
      });
    }

    // Now update Firestore with the new URL
    await setDoc(doc(db, 'users', auth.currentUser.uid), {
      ...userData,
      profilePhoto: profilePhotoUrl,
      updatedAt: new Date().toISOString(),
    }, { merge: true });

    navigate('/profile');
  } catch (err) {
    setError('Update failed. Please try again.');
  } finally {
    setIsLoading(false);
    setUploadProgress(0);
  }
};

  if (isLoading) {
    return (
      <Box sx={styles.loader}>
        <CircularProgress size={40} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Header with Back Button */}
      <Box sx={styles.header}>
        <IconButton onClick={() => navigate('/profile')} sx={styles.backButton}>
          <ArrowBackIcon sx={{ color: '#FFF' }} />
        </IconButton>
        <Typography variant="h5" sx={styles.title}>
          Edit Profile
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={styles.contentContainer}>
        <Card sx={styles.card}>
          {/* Profile Photo Section */}
          <Box sx={styles.profilePhotoContainer}>
            <Avatar
              src={
                selectedFile
                  ? URL.createObjectURL(selectedFile)
                  : userData.profilePhoto || 'https://via.placeholder.com/100/C00068/FFFFFF?text=P'
              }
              sx={styles.profileAvatar}
              onError={(e) => { e.target.src = 'https://via.placeholder.com/100/C00068/FFFFFF?text=P'; }}
            />
            <Button
              variant="contained"
              component="label"
              sx={styles.imageButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: '#FFF' }} />
              ) : (
                userData.profilePhoto || selectedFile ? 'Change Profile Photo' : 'Upload Profile Photo'
              )}
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                sx={{ display: 'none' }}
              />
            </Button>
            {uploadProgress > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CircularProgress variant="determinate" value={uploadProgress} size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">{Math.round(uploadProgress)}%</Typography>
              </Box>
            )}
          </Box>

          {/* Form Fields */}
          <TextField
            label="First Name"
            value={userData.firstName}
            onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
            sx={styles.input}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={userData.lastName}
            onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
            sx={styles.input}
            fullWidth
          />
          <TextField
            label="Bio"
            value={userData.bio}
            onChange={(e) => setUserData({ ...userData, bio: e.target.value })}
            sx={styles.input}
            fullWidth
            multiline
            rows={3}
          />
          <FormControl sx={styles.input} fullWidth>
            <InputLabel>Select Sex</InputLabel>
            <Select
              value={userData.sex}
              onChange={(e) => setUserData({ ...userData, sex: e.target.value })}
              label="Select Sex"
            >
              <MenuItem value="">Select Sex</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth"
              value={userData.dob}
              onChange={(date) => setUserData({ ...userData, dob: date })}
              renderInput={(params) => <TextField {...params} sx={styles.input} fullWidth />}
              inputFormat="yyyy-MM-dd"
            />
          </LocalizationProvider>
          <TextField
            label="Phone Number"
            value={userData.phoneNumber}
            onChange={(e) => setUserData({ ...userData, phoneNumber: e.target.value })}
            sx={styles.input}
            fullWidth
          />
          <TextField
            label="Address"
            value={userData.address}
            onChange={(e) => setUserData({ ...userData, address: e.target.value })}
            sx={styles.input}
            fullWidth
          />
          {error && <FormHelperText sx={styles.error}>{error}</FormHelperText>}
          <Box sx={{ display: 'flex', gap: '10px', mt: '20px' }}>
            <Button
              variant="contained"
              onClick={handleSaveProfile}
              sx={styles.gradientButton}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: '#FFF' }} /> : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/profile')}
              sx={styles.cancelButton}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </Box>
        </Card>
      </Box>
      {uploadProgress > 0 && (
  <Box sx={{ width: '100%', mt: 2 }}>
    <Typography variant="body2" color="white" align="center">
      Uploading Profile Photo... {Math.round(uploadProgress)}%
    </Typography>
    <LinearProgress 
      variant="determinate" 
      value={uploadProgress} 
      sx={{ 
        height: 10, 
        borderRadius: 5,
        color: 'white',
        backgroundColor: '#eee',
        '& .MuiLinearProgress-bar': {
          background: 'linear-gradient(to right, #B200BB, #CF003D)',
        }
      }} 
    />
  </Box>
)}
      <BottomNavigationBar />
    </Box>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(to bottom, #B200BB, #CF003D)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    p: '10px 16px',
    background: 'linear-gradient(to right, #B200BB, #C00068)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  backButton: {
    mr: '10px',
  },
  title: {
    color: '#FFF',
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    p: '20px',
    overflowY: 'auto',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    p: '20px',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 0 5px rgba(0,0,0,0.2)',
    opacity: 0.9,
  },
  profilePhotoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    mb: '20px',
  },
  profileAvatar: {
    width: '100px',
    height: '100px',
    mb: '10px',
    border: '3px solid #C00068',
  },
  input: {
    mb: '10px',
  },
  error: {
    color: 'red',
    mb: '10px',
    textAlign: 'center',
  },
  imageButton: {
    background: 'linear-gradient(to right, #B91372, #6B0F1A)',
    borderRadius: '25px',
    p: '10px 15px',
    boxShadow: '0 0 10px rgba(0,0,0,0.2)',
    textTransform: 'none',
    color: '#FFF',
    fontWeight: 'bold',
  },
  gradientButton: {
    borderRadius: '25px',
    p: '12px',
    background: 'linear-gradient(to right, #C00068, #FF416C)',
    boxShadow: '0 0 5px rgba(0,0,0,0.2)',
    textTransform: 'none',
    color: '#FFF',
    fontWeight: 'bold',
    flex: 1,
  },
  cancelButton: {
    borderRadius: '25px',
    p: '12px',
    borderColor: '#FFF',
    color: '#FFF',
    textTransform: 'none',
    flex: 1,
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(to bottom, #B200BB, #CF003D)',
  },
};

export default SettingsScreen;